/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-explicit-any */

/*
 [PT] [WBF-5854] 2/5/18
 ======================
 Keys map onto 2 values
 in an attempt to provide business with
 a seamless transition to more sensible
 identifiers in GTM. If and when they
 buy into those more sensible names, the
 the second array element (preferred name)
 in each array can replace the entire array. */
const FIELD_NAME_TO_DATA_LAYER_NAMES: {[key: string]: string[] | undefined} = {
    action: ["pageName", "action"],
    cd9:    ["contentType", "page"],
    cd13:   ["pageType", "flow"],
    cd14:   ["template"],
    cd19:   ["product"],
    cd26:   ["resourceSubType"],
    cd58:   ["breg"]
};

interface DataLayerOptions {
    [key: string]: string | undefined;
    pageName?: string;
    pageType?: string;
    contentType?: string;
    template?: string;
}

interface TrackingParams extends DataLayerOptions {
    [key: string]: string| undefined;
    pageTitle?: string;
    refresh?: string;
}

interface DataLayerFields {
    [key: string]: string | undefined;
    eventAction?: string;
    eventLabel?: string;
    eventCategory?: string;
    action?: string;
    cd9?: string;
    cd13?: string;
    cd26?: string;
    cd58?: string;
}

interface EventDetails {
    [key: string]: string | undefined;
    event: string;
    keyweeEventName?: string;
}

export class GoogleTracking {
    pushEvent(e: any): GoogleTracking {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(e);

        return this;
    }

    setDataLayerFields(fields: DataLayerFields): GoogleTracking {
        const correctedFields: {[key: string]: string | undefined} = {};

        Object.keys(fields).forEach((field: string): void => {
            const correctedFieldNames = FIELD_NAME_TO_DATA_LAYER_NAMES[field];
            const fieldValue = fields[field];

            if (correctedFieldNames) {
                correctedFieldNames.forEach((name: string) => {
                    correctedFields[name] = fieldValue;
                });
            } else {
                correctedFields[field] = fieldValue;
            }
        });

        return this.pushEvent(correctedFields);
    }

    /*
     [PT] [WBF-5854] 2/5/18
     ======================
     Try to deprecate this function after v2 flow is launched. */

    updateDataLayer(data?: DataLayerOptions): GoogleTracking | undefined {
        if (!data) {
            return;
        }

        const trackingData = this.getTrackingParams(data);

        return this.pushEvent(trackingData);
    }

    recordPageView(): GoogleTracking {
        return this.pushEvent({
            event: "Track Request - PV"
        });
    }

    registerClickEvent(className: string, elementName: string): GoogleTracking {
        return this.pushEvent({
            event: "gtm.click", "gtm.elementClasses": className, "gtm.element": elementName
        });
    }

    registerErrorEvent(errorDetails: string): GoogleTracking {
        return this.pushEvent({
            event: "gtm.pageError", "gtm.elementClasses": "reg-error", regErrorDetails: errorDetails
        });
    }

    registerCompletionEvent(className: string, elementName: string): GoogleTracking {
        return this.pushEvent({
            event: "Registration Complete", "gtm.elementClasses": className, "gtm.element": elementName
        });
    }

    registerSubscriptionEvent(eventAction: string, eventLabel: string): GoogleTracking {
        this.setDataLayerFields({
            eventAction,
            eventLabel
        });

        return this.pushEvent({
            event: "Subscription Event"
        });
    }

    registerSignInEvent(elementName: string): GoogleTracking {
        return this.pushEvent({
            event: "Registration Login Success", "gtm.elementClasses": elementName, "gtm.element": elementName
        });
    }

    registerCommercialTransaction(
        { accountId, price, productName, productCategory, productId, tax, offerType, billingPeriod }:
        {
            accountId: string;
            price: string;
            productName: string;
            productCategory: string;
            productId: string;
            tax: string;
            offerType: string;
            billingPeriod: string;
        }): GoogleTracking {
        return this.pushEvent({
            event: "Subscription Purchase Event",
            transactionId: accountId,
            transactionTotal: price,
            transactionTax: tax,
            transactionProducts: [{
                sku: productId,
                name: productName,
                category: productCategory,
                price,
                quantity: 1
            }],
            transactionProductId: productId,
            transactionProductName: productName,
            transactionProductCategory: productCategory,
            transactionOfferType: offerType,
            transactionBillingPeriod: billingPeriod
        });
    }

    registerOnboardingEvent(eventAction: string, eventLabel: string): void {
        this.setDataLayerFields({
            eventAction,
            eventLabel,
            event: "onboarding"
        });
    }

    registerSaveOfferEvent(eventLabel: string): void {
        this.setDataLayerFields({
            event: "saveOffer",
            eventCategory: "registration",
            eventAction: "click",
            eventLabel
        });
    }

    registerEvent(event: EventDetails): GoogleTracking {
        return this.pushEvent(event);
    }

    /*
     [PT] [WBF-5854] 2/5/18
     ======================
     Try to deprecate this function after v2 flow is launched. */

    getTrackingParams(data?: DataLayerOptions): TrackingParams | undefined {
        if (!data) {
            return data;
        }

        const pageName = data.pageName;
        const pageType = data.pageType || "login";

        const trackingData: TrackingParams = {
            pageName,
            pageType,
            contentType: data.contentType || `${ pageType }-${ pageName || "" }`,
            template: data.template || getTemplateFrom(pageType),
            refresh: "false",
            pageTitle: pageName
        };

        Object.keys(trackingData).forEach(function(k: string) {
            if (!trackingData[k]) {
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete trackingData[k];
            }
        });

        return trackingData;
    }

    // Exposing the ABBA data so GTM could track it
    exposeAbbaConfig(abbaConfig: AbbaConfig): void {
        window.__bloomberg__ = window.__bloomberg__ || {};
        window.__bloomberg__.breg = window.__bloomberg__.breg || {};
        window.__bloomberg__.breg.abbaConfig = abbaConfig;
    }
}

function getTemplateFrom(pageType: string): string {
    return `login-${ pageType }-html`;
}

export default new GoogleTracking;
